/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Panel, PanelBody, PanelHeader } from '../../../util/panel';
import { Button, Input, Select, Table } from 'antd';
import { FaPen, FaPlus, FaSearch } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import usePaginationHook from '../../../../utils/usePagination';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AlertConfirm } from '../../../util/Alert';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import 'dayjs/locale/th';
import fitMissionServices from '../../../../services/fit-mission.services';
import LegacyRewardModal from '../components/modal/LegacyRewardModal';
import { useHistory } from 'react-router';

dayjs.locale('th');

const pageSize = 10;

const inStockOptions = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'true', label: 'สินค้าพร้อมแลก' },
  { value: 'false', label: 'สินค้าหมดสต็อก' },
];

const LegacyRewardPage = () => {
  const [legacyRewardFormOpen, setLegacyRewardFormOpen] = useState(false);

  const {
    data: legacyRewardLists,
    setData,
    page,
    setPage,
    total,
    setTotal,
  } = usePaginationHook();
  const queryClient = useQueryClient();
  const history = useHistory();

  const filterForm = useForm({
    defaultValues: {
      searchText: '',
      inStock: 'all',
    },
  });

  const { watch: watchFilter } = filterForm;
  const searchText = watchFilter('searchText');
  const inStock = watchFilter('inStock');

  const legacyRewardForm = useForm({
    defaultValues: {
      formType: 'create',
      id: undefined,
      title: '',
      image: '',
      point: 0,
      sku: '',
      stockQuantity: 0,
    },
  });

  const _HandleLegacyRewardFormOpen = (formType) => {
    legacyRewardForm.setValue('formType', formType);
    setLegacyRewardFormOpen(true);
  };

  const _HandleLegacyRewardFormClose = () => {
    legacyRewardForm.reset();
    setLegacyRewardFormOpen(false);
  };

  fitMissionServices.useQueryGetMissionRewards(
    {
      take: pageSize,
      page,
      searchText: searchText || undefined,
      inStock: inStock === 'all' ? undefined : inStock === 'true',
    },
    (data) => {
      setData(data.data);
      setTotal(data.meta.total);
    },
  );

  const handleFilterChange = () => {
    setPage(1);
  };

  const { mutate: deleteMissionReward } =
    fitMissionServices.useMutationDeleteMissionReward(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-mission-reward'],
        refetchType: 'all',
      });
    });

  const columns = [
    {
      title: 'NO',
      dataIndex: 'index',
      key: 'index',
      render: (_, record, index) => (
        <span>{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: 'รูปสินค้า',
      dataIndex: 'image',
      key: 'image',
      render: (_, record) => (
        <div
          style={{
            width: 200,
            height: 100,
          }}
        >
          {record.image && (
            <img
              alt={record.title}
              src={record.image}
              style={{
                width: 200,
                height: 100,
                objectFit: 'cover',
              }}
            />
          )}
        </div>
      ),
    },
    {
      title: 'รหัสสินค้า',
      dataIndex: 'sku',
      key: 'sku',
      render: (_, record) => <span>{record.sku}</span>,
      width: 250,
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => <span>{record.title}</span>,
      width: 250,
    },
    {
      title: 'คะแนนที่ใช้แลก',
      dataIndex: 'point',
      key: 'point',
      render: (_, record) => <span>{record.point}</span>,
    },
    {
      title: 'จำนวนสต็อก',
      dataIndex: 'stockQuantity',
      key: 'stockQuantity',
      render: (_, record) => <span>{record.stockQuantity}</span>,
    },
    {
      title: 'อัปเดตเมื่อ',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_, record) => (
        <span>{dayjs(record.updatedAt).format('DD/MMM/YYYY')}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.updatedAt).diff(dayjs(b.updatedAt), 'D') > 0 ? 1 : -1,
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={() => {
              legacyRewardForm.setValue('id', record.id);
              legacyRewardForm.setValue('title', record.title);
              legacyRewardForm.setValue('description', record.description);
              legacyRewardForm.setValue('image', record.image);
              legacyRewardForm.setValue('point', record.point);
              _HandleLegacyRewardFormOpen('edit');
            }}
          >
            <FaPen />
          </div>
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={async () => {
              const confirm = await AlertConfirm('ลบข้อมูล');
              if (confirm) deleteMissionReward({ id: record.id });
            }}
          >
            <MdDelete />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Panel>
      <PanelHeader>จัดการแลกสินค้า</PanelHeader>
      <PanelBody>
        <div style={{ display: 'flex', alignItems: 'center' }} className="mb-2">
          <FormProvider {...filterForm}>
            <Controller
              name="searchText"
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="ค้นหารหัสสินค้า, ชื่อสินค้า"
                  prefix={<FaSearch />}
                  onChange={(e) => {
                    field.onChange(e);
                    handleFilterChange();
                  }}
                  style={{ width: 250, marginRight: 16 }}
                  allowClear
                />
              )}
            />
            <Controller
              name="inStock"
              render={({ field }) => (
                <Select
                  {...field}
                  onChange={(value) => {
                    field.onChange(value);
                    handleFilterChange();
                  }}
                  style={{ width: 150 }}
                  placeholder="สถานะสต็อก"
                >
                  {inStockOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            />
          </FormProvider>
          <div style={{ flex: 1 }} />
          <Button
            onClick={() => {
              _HandleLegacyRewardFormOpen('create');
            }}
            type="primary"
          >
            <FaPlus className="mr-2" />
            เพิ่มสินค้าแลก
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={legacyRewardLists}
          style={{ backgroundColor: '#fff' }}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          onChange={({ current }) => {
            setPage(current);
          }}
          pagination={{
            pageSize,
            total,
          }}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                history.push(`/mission/reward/${record.id}`);
              },
            };
          }}
        />
      </PanelBody>
      <FormProvider {...legacyRewardForm}>
        <LegacyRewardModal
          open={legacyRewardFormOpen}
          handleClose={_HandleLegacyRewardFormClose}
        />
      </FormProvider>
    </Panel>
  );
};

export default LegacyRewardPage;
